import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Avatar from '@mui/material/Avatar';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';
import { handleLogout } from '../../utils/auth';
import { fetchAllCertificates } from '../apis/certificateService';
import DataTable from './DataTable';
import '../admin/styling/Dashboard.css';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  useEffect(() => {
    const loadCertificates = async () => {
      try {
        const data = await fetchAllCertificates();
        setCertificates(data);
        setLastUpdated(new Date().toLocaleString());
        setError(null);
      } catch (err) {
        setError('Failed to load certificates. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    loadCertificates();
  }, []);

  // ✅ Compute course counts
  const courseCounts = certificates.reduce((acc, item) => {
    const course = item.course?.toUpperCase() || "UNKNOWN";
    acc[course] = (acc[course] || 0) + 1;
    return acc;
  }, {});

  const courses = ["TALLY", "DCA", "ADCA", "DTP", "PGDCA", "BCC"];

  return (
    <div className="dashboard-bg">
      <div className="dashboard-card">

        {/* Header */}
        <div className="dashboard-header">
        <div className="dashboard-header-left">
            <Avatar className="dashboard-avatar" sx={{ bgcolor: '#22b2dd', width: 56, height: 56 }}>
            <WorkspacePremiumIcon fontSize="large" />
            </Avatar>

            <div>
            <Typography variant="h4" className="dashboard-title">
                Certificate Management
            </Typography>
            <Typography variant="subtitle2" className="dashboard-subtitle">
                View and manage all issued certificates
            </Typography>
            </div>
        </div>

        <Box display="flex" gap={2}>
            <Button
            variant="contained"
            color="success"
            onClick={() => navigate('/admin/add-certificate')}
            className="dashboard-addNew-btn"
            >
            + Add New
            </Button>

            <Button 
            variant="contained" 
            color="primary" 
            onClick={() => {
                handleLogout();
                navigate("/admin/login");
            }}
            className="dashboard-logout-btn"
            >
            Logout
            </Button>
        </Box>
        </div>


        <Divider sx={{ my: 2 }} />

        {/* Summary Section */}
        {!loading && !error && (
          <>
            <Paper elevation={1} sx={{ p: 2, mb: 2, borderRadius: 2 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="h6" fontWeight="bold">
                  Total Certificates: <span style={{ color: '#1976d2' }}>{certificates.length}</span>
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Last Updated: {lastUpdated}
                </Typography>
              </Box>
            </Paper>

            {/* Course Breakdown */}
            <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 2 }}>
              <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                Breakdown by Course
              </Typography>

              <Box display="flex" flexWrap="wrap" gap={2}>
                {courses.map(course => (
                 <Box key={course} className="course-card-box">

                    <Typography variant="body2" fontWeight="600">{course}</Typography>
                    <Typography variant="h6" sx={{ color: "#1976d2", fontWeight: "bold" }}>
                      {courseCounts[course] || 0}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Paper>
          </>
        )}

        {/* Error */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Loader / Table */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <DataTable data={certificates} />
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
